import {type ComponentProps, forwardRef} from 'react'
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronSelectorVerticalIcon,
    ChevronUpIcon
} from '@components/ui/icon'
import {Skeleton} from '@/components/ui/skeleton'
import {Button} from '@components/ui/button'
import {cn} from '@utilities/helpers'
import {Trans} from '@/translations/i18n'

export type OrderDirection = 'asc' | 'desc'

export interface Order<T extends string> {
    orderBy?: T
    orderDirection?: OrderDirection
}

const getPinningStyle = (
    pinningPosition: 'left' | 'right' | false,
    pinningStart: number
): {left?: number; right?: number} | undefined => {
    if (pinningPosition === 'left') {
        return {left: pinningStart}
    }
    if (pinningPosition === 'right') {
        return {right: pinningStart}
    }
    return undefined
}

const getSortIcon = (orderDirection: `${OrderDirection}` | null) => {
    if (orderDirection === 'asc') {
        return <ChevronUpIcon />
    }
    if (orderDirection === 'desc') {
        return <ChevronDownIcon />
    }
    return <ChevronSelectorVerticalIcon />
}

export const tableBackgroundClassName = 'bg-white' as const
export const tableRowBackgroundHoverClassName = 'hover:bg-neutral-50' as const
export const tableRowBackgroundHoverGroupClassName = 'group-hover/row:bg-neutral-50' as const
export const tableRowBackgroundSelectedGroupClassName = 'group-data-[selected]/row:bg-neutral-100' as const

const TableRoot = forwardRef<HTMLTableElement, ComponentProps<'table'>>(({className, ...props}, ref) => (
    <table
        className={cn('isolate w-full table-fixed border-spacing-0', tableBackgroundClassName, className)}
        ref={ref}
        {...props}
    />
))

const TableHeader = forwardRef<HTMLTableSectionElement, ComponentProps<'thead'>>(({className, ...props}, ref) => (
    <thead className={cn('sticky top-0 isolate z-2', tableBackgroundClassName, className)} ref={ref} {...props} />
))

const TableHeaderRow = forwardRef<HTMLTableRowElement, ComponentProps<'tr'>>(({className, ...props}, ref) => (
    <tr className={cn('relative [&>th]:border-neutral-200 [&>th]:border-b', className)} ref={ref} {...props} />
))

function TableHeaderGapRow({columnsLength, ...props}: ComponentProps<'tr'> & {columnsLength: number}) {
    return (
        <tr className={cn('h-1 w-full', tableBackgroundClassName)} {...props}>
            <th className="border-neutral-200 border-b" colSpan={columnsLength} />
        </tr>
    )
}

const TableHeaderCell = forwardRef<
    HTMLTableCellElement,
    ComponentProps<'th'> & {
        pinningPosition?: 'left' | 'right' | false
        pinningStart?: number
    }
>(({className, children, pinningPosition = false, pinningStart = 0, style, ...props}, ref) => (
    <th
        className={cn(
            'group/header-cell h-11 overflow-x-hidden px-3 first:pl-6 last:pr-6',
            'bg-white text-left align-middle',
            pinningPosition ? 'sticky z-3' : 'relative',
            className
        )}
        ref={ref}
        style={{
            ...getPinningStyle(pinningPosition, pinningStart),
            ...style
        }}
        {...props}
    >
        <div className="flex items-center justify-between gap-1">{children}</div>
    </th>
))

function TableHeaderCellLabel({className, ...props}: ComponentProps<'span'>) {
    return (
        <span
            className={cn('w-full select-none truncate font-medium text-neutral-600 text-sm', className)}
            {...props}
        />
    )
}

function TableResizer(props: ComponentProps<'div'>) {
    return (
        <div
            className={cn(
                'touch-action-none user-select-none absolute top-0 right-0 h-full w-1 cursor-col-resize transition duration-200 ease-in-out',
                'opacity-0 group-hover/header-cell:opacity-100',
                'bg-primary-400 hover:bg-primary-600'
            )}
            {...props}
        />
    )
}

function TableSortButton({
    orderDirection,
    ...props
}: ComponentProps<typeof Button> & {
    orderDirection: `${OrderDirection}` | null
}) {
    return (
        <Button size="xs" shape="square" variant="ghost" {...props}>
            {getSortIcon(orderDirection)}
        </Button>
    )
}

const TableBody = forwardRef<HTMLTableSectionElement, ComponentProps<'tbody'>>(({className, ...props}, ref) => (
    <tbody className={cn('isolate', className)} ref={ref} {...props} />
))

const TableRow = forwardRef<HTMLTableRowElement, ComponentProps<'tr'>>(({className, ...props}, ref) => (
    <tr
        className={cn(
            'group/row relative bg-white transition duration-200 ease-in-out',
            'not-last:[&>td]:border-neutral-200 not-last:[&>td]:border-b',
            'data-[selected]:bg-neutral-50',
            className
        )}
        ref={ref}
        {...props}
    />
))

const TableCell = forwardRef<
    HTMLTableCellElement,
    ComponentProps<'td'> & {
        pinningPosition?: 'left' | 'right' | false
        pinningStart?: number
    }
>(({className, pinningPosition = false, pinningStart = 0, style, ...props}, ref) => (
    <td
        className={cn(
            'h-18 px-3 text-left align-middle text-neutral-700 text-sm first:pl-6 last:pr-6',
            pinningPosition
                ? cn(
                      'sticky z-1 transition duration-200 ease-in-out',
                      'bg-white group-data-[selected]/row:bg-neutral-25'
                  )
                : 'relative',
            className
        )}
        ref={ref}
        style={{
            ...getPinningStyle(pinningPosition, pinningStart),
            ...style
        }}
        {...props}
    />
))

function TableCellSkeleton({className, ...props}: ComponentProps<typeof Skeleton>) {
    return <Skeleton className={cn('h-10 w-full', className)} {...props} />
}

/**
 * Row rendered after its parent row when expanded. Borders are left to the
 * expanded content, not imposed here.
 */
function TableExpandableRow({
    isExpanded,
    colSpan,
    className,
    children,
    ...props
}: ComponentProps<'tr'> & {isExpanded: boolean; colSpan: number}) {
    if (!isExpanded) {
        return null
    }

    return (
        <tr className="bg-white" {...props}>
            <td className={cn('border-neutral-200 p-0', className)} colSpan={colSpan}>
                {children}
            </td>
        </tr>
    )
}

const bodyCellTextVariantClassName = {
    light: 'text-neutral-400',
    normal: 'text-neutral-700',
    dark: 'text-neutral-950'
} as const

const TableBodyCellText = forwardRef<
    HTMLParagraphElement,
    ComponentProps<'p'> & {variant?: 'light' | 'normal' | 'dark'}
>(({className, variant = 'normal', ...props}, ref) => (
    <p
        className={cn(
            'overflow-x-hidden text-ellipsis whitespace-nowrap text-sm',
            bodyCellTextVariantClassName[variant],
            className
        )}
        ref={ref}
        {...props}
    />
))

function PaginationPrevious({...props}: React.ComponentProps<typeof Button>) {
    return (
        <Button type="button" shape="square" size="sm" variant="ghost" {...props}>
            <ChevronLeftIcon size={16} />
        </Button>
    )
}

function PaginationNext({...props}: React.ComponentProps<typeof Button>) {
    return (
        <Button type="button" shape="square" size="sm" variant="ghost" {...props}>
            <ChevronRightIcon size={16} />
        </Button>
    )
}

export interface TablePaginationProps {
    pagination: {
        limit: number
        currentPage: number
        total: number
        onChangePage: (page: number) => void
    }
}

function TablePagination({pagination}: TablePaginationProps) {
    const lastPage = pagination.total >= pagination.limit ? Math.ceil(pagination.total / pagination.limit) : 1

    return (
        <div className="flex items-center justify-between border-neutral-200 border-t bg-white py-3">
            <div className="flex shrink-0 items-center gap-1 text-neutral-600 text-sm [&_b]:font-medium [&_b]:text-neutral-950">
                <Trans
                    components={{b: <b />}}
                    i18nKey="commons:show_x_of_y_rows"
                    values={{
                        count: pagination.total,
                        fromCount: pagination.currentPage === 1 ? 1 : pagination.limit * (pagination.currentPage - 1),
                        toCount:
                            pagination.limit * pagination.currentPage > pagination.total
                                ? pagination.total
                                : pagination.limit * pagination.currentPage
                    }}
                />
            </div>
            <div className="flex items-center justify-end gap-0.5">
                <PaginationPrevious
                    aria-label="Go to previous page"
                    disabled={pagination.currentPage === 1}
                    onClick={() => pagination.onChangePage(pagination.currentPage - 1)}
                />
                <PaginationNext
                    aria-label="Go to next page"
                    disabled={pagination.currentPage === lastPage}
                    onClick={() => pagination.onChangePage(pagination.currentPage + 1)}
                />
            </div>
        </div>
    )
}

export {
    TableRoot,
    TableHeader,
    TableHeaderRow,
    TableHeaderGapRow,
    TableHeaderCell,
    TableHeaderCellLabel,
    TableSortButton,
    TableResizer,
    TableBody,
    TableRow,
    TableCell,
    TableCellSkeleton,
    TableExpandableRow,
    TableBodyCellText,
    TablePagination
}
